import dayjs from "dayjs";
import { useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CustomDatePicker } from "@/components/common/CustomDatePicker";
import { CustomTimePicker } from "@/components/common/CustomTimePicker";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import type { EventFormData } from "@/features/study-group-events/type";
import { cn } from "@/lib/utils";
import {
  getDateTimeValidationMessage,
  validateDateTimeOrder,
} from "@/lib/utils/eventUtils";

interface EventDateSectionProps {
  eventData: EventFormData;
  onChange: (data: EventFormData) => void;
  className?: string;
  showLabels?: boolean;
  dirtyClassNameFor?: (key: keyof EventFormData) => string;
}

export const EventDateSection = ({
  eventData,
  onChange,
  className = "",
  showLabels = true,
  dirtyClassNameFor,
}: EventDateSectionProps) => {
  const { t } = useTranslation();
  const startDateId = useId();
  const endDateId = useId();
  const startTimeId = useId();
  const endTimeId = useId();
  const allDayId = useId();
  const startDateLabelId = useId();
  const endDateLabelId = useId();

  const parseDateTime = (date?: string, time?: string) => {
    if (!date || !time) return null;
    const dateTime = dayjs(`${date}T${time}`);
    return dateTime.isValid() ? dateTime : null;
  };

  const adjustDateTime = (
    data: EventFormData,
    anchor: "start" | "end",
  ): EventFormData => {
    if (data.isAllDay) return data;
    if (!data.startDate || !data.startTime || !data.endDate || !data.endTime)
      return data;

    const start = parseDateTime(data.startDate, data.startTime);
    const end = parseDateTime(data.endDate, data.endTime);

    if (!start || !end) return data;
    if (end.isAfter(start)) return data;

    if (anchor === "start") {
      const newEnd = start.add(1, "hour");
      return {
        ...data,
        endDate: newEnd.format("YYYY-MM-DD"),
        endTime: newEnd.format("HH:mm"),
      };
    }

    const newStart = end.subtract(1, "hour");
    return {
      ...data,
      startDate: newStart.format("YYYY-MM-DD"),
      startTime: newStart.format("HH:mm"),
    };
  };

  const derivedError = useMemo(() => {
    if (
      eventData.isAllDay ||
      !eventData.startDate ||
      !eventData.startTime ||
      !eventData.endDate ||
      !eventData.endTime
    ) {
      return "";
    }

    const { isValid, errorType } = validateDateTimeOrder(
      eventData.startDate,
      eventData.startTime,
      eventData.endDate,
      eventData.endTime,
    );

    if (!isValid && errorType) {
      return getDateTimeValidationMessage(errorType, t);
    }

    return "";
  }, [
    eventData.isAllDay,
    eventData.startDate,
    eventData.startTime,
    eventData.endDate,
    eventData.endTime,
    t,
  ]);

  return (
    <div className={cn("space-y-4", className)}>
      {/* 終日トグル */}
      <div className="flex items-center space-x-2">
        <Switch
          id={allDayId}
          checked={eventData.isAllDay}
          onCheckedChange={(checked) =>
            onChange({ ...eventData, isAllDay: checked })
          }
        />
        <Label htmlFor={allDayId} className="text-sm font-medium">
          {t("common.allDay")}
        </Label>
      </div>

      {/* 日付・時刻入力セクション */}
      <div className="space-y-4">
        {/* 開始日時 */}
        <div>
          {showLabels && (
            <Label
              id={startDateLabelId}
              className="mb-1 block text-sm font-medium"
            >
              {t("common.startDate")}*
            </Label>
          )}
          <div className="flex gap-4">
            <div className="flex-1">
              <CustomDatePicker
                id={startDateId}
                ariaLabel={t("common.startDate")}
                labelId={startDateLabelId}
                value={eventData.startDate}
                onChange={(startDate) =>
                  onChange(adjustDateTime({ ...eventData, startDate }, "start"))
                }
                className="w-full"
                side="top"
                triggerClassName={cn(
                  "w-full",
                  dirtyClassNameFor?.("startDate"),
                )}
              />
            </div>
            {!eventData.isAllDay && (
              <div className="flex-1">
                <CustomTimePicker
                  id={startTimeId}
                  value={eventData.startTime}
                  onChange={(startTime) =>
                    onChange(
                      adjustDateTime({ ...eventData, startTime }, "start"),
                    )
                  }
                  className="w-full"
                  triggerClassName={cn(
                    "w-full",
                    dirtyClassNameFor?.("startTime"),
                  )}
                />
              </div>
            )}
          </div>
        </div>

        {/* 終了日時 */}
        <div>
          {showLabels && (
            <Label
              id={endDateLabelId}
              className="mb-1 block text-sm font-medium"
            >
              {t("common.endDate")}*
            </Label>
          )}
          <div className="flex gap-4">
            <div className="flex-1">
              <CustomDatePicker
                id={endDateId}
                ariaLabel={t("common.endDate")}
                labelId={endDateLabelId}
                value={eventData.endDate}
                onChange={(endDate) =>
                  onChange(adjustDateTime({ ...eventData, endDate }, "end"))
                }
                className="w-full"
                side="top"
                triggerClassName={cn("w-full", dirtyClassNameFor?.("endDate"))}
              />
            </div>
            {!eventData.isAllDay && (
              <div className="flex-1">
                <CustomTimePicker
                  id={endTimeId}
                  value={eventData.endTime}
                  onChange={(endTime) =>
                    onChange(adjustDateTime({ ...eventData, endTime }, "end"))
                  }
                  className="w-full"
                  triggerClassName={cn(
                    "w-full",
                    dirtyClassNameFor?.("endTime"),
                  )}
                />
              </div>
            )}
          </div>
        </div>

        {/* バリデーションエラー */}
        {derivedError && (
          <p className="mt-1 text-sm text-red-500">{derivedError}</p>
        )}
      </div>
    </div>
  );
};
